import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Heart, Brain, Battery, Moon, Users, Clock, Smile, Activity,
  AlertTriangle, CheckCircle, Loader2, ChevronRight, Phone,
  BookOpen, MessageCircle, RefreshCw, TrendingUp
} from "lucide-react";

const QUESTIONS = [
  { key: "physical_exhaustion", label: "Physical Exhaustion", description: "How physically tired do you feel from caregiving?", icon: Battery, lowLabel: "Not tired", highLabel: "Very exhausted" },
  { key: "emotional_exhaustion", label: "Emotional Exhaustion", description: "How emotionally drained do you feel?", icon: Heart, lowLabel: "Energized", highLabel: "Very drained" },
  { key: "sleep_quality", label: "Sleep Quality", description: "How well have you been sleeping?", icon: Moon, lowLabel: "Very poor", highLabel: "Excellent" },
  { key: "social_isolation", label: "Social Isolation", description: "How isolated do you feel from friends and family?", icon: Users, lowLabel: "Very connected", highLabel: "Very isolated" },
  { key: "caregiving_burden", label: "Caregiving Burden", description: "How overwhelmed do you feel by caregiving responsibilities?", icon: Activity, lowLabel: "Manageable", highLabel: "Overwhelming" },
  { key: "self_care_time", label: "Self-Care Time", description: "How much time do you have for yourself?", icon: Smile, lowLabel: "No time", highLabel: "Plenty of time" },
  { key: "support_system", label: "Support System", description: "How supported do you feel by others?", icon: Heart, lowLabel: "No support", highLabel: "Very supported" },
  { key: "overall_stress", label: "Overall Stress Level", description: "How would you rate your overall stress?", icon: Brain, lowLabel: "Very calm", highLabel: "Extremely stressed" },
];

const RISK_COLORS = {
  low: "bg-green-500",
  moderate: "bg-yellow-500",
  high: "bg-orange-500",
  critical: "bg-red-500"
};

const RISK_TEXT_COLORS = {
  low: "text-green-600",
  moderate: "text-yellow-600",
  high: "text-orange-600",
  critical: "text-red-600"
};

export default function CaregiverBurnoutAssessment({ onComplete }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0); // 0 = intro, 1-8 = questions, 9 = notes, 10 = results
  const [answers, setAnswers] = useState({
    physical_exhaustion: 3,
    emotional_exhaustion: 3,
    sleep_quality: 3,
    social_isolation: 3,
    caregiving_burden: 3,
    self_care_time: 3,
    support_system: 3,
    overall_stress: 3,
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await api.get("/burnout/my-assessments?limit=5");
      setHistory(res.data.assessments || []);
    } catch (error) {
      console.error("Failed to load history:", error);
    }
  };

  const handleAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value[0] }));
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/burnout/assessment", {
        ...answers,
        notes: notes || null
      });
      setResult(res.data);
      setStep(10);
      loadHistory();
      if (onComplete) onComplete(res.data);
    } catch (error) {
      toast.error("Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAssessment = () => {
    setStep(0);
    setAnswers({
      physical_exhaustion: 3,
      emotional_exhaustion: 3,
      sleep_quality: 3,
      social_isolation: 3,
      caregiving_burden: 3,
      self_care_time: 3,
      support_system: 3,
      overall_stress: 3,
    });
    setNotes("");
    setResult(null);
  };

  const currentQuestion = QUESTIONS[step - 1];

  // Intro Screen
  if (step === 0) {
    return (
      <Card className="max-w-2xl mx-auto" data-testid="burnout-assessment">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Caregiver Wellness Check</CardTitle>
          <CardDescription className="text-base mt-2">
            Taking care of yourself is essential for providing good care to others.
            This brief assessment helps identify signs of caregiver burnout.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-medium mb-2">What to expect:</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• 8 quick questions about your wellbeing</li>
              <li>• Takes about 2-3 minutes to complete</li>
              <li>• Personalized recommendations based on your responses</li>
              <li>• Resources to help you manage stress</li>
            </ul>
          </div>

          {history.length > 0 && (
            <div className="border-t pt-4">
              <Button variant="ghost" className="w-full justify-between" onClick={() => setShowHistory(true)}>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  View Previous Assessments ({history.length})
                </span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          <Button className="w-full" size="lg" onClick={() => setStep(1)} data-testid="start-assessment-btn">
            Start Assessment
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>

        {/* History Dialog */}
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assessment History</DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {history.map((assessment) => (
                  <div key={assessment.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={RISK_COLORS[assessment.risk_level]}>
                        {assessment.risk_level.toUpperCase()}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Score: {assessment.total_score}/40
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(assessment.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  // Question Screen (steps 1-8)
  if (step >= 1 && step <= 8) {
    const Icon = currentQuestion.icon;
    return (
      <Card className="max-w-2xl mx-auto" data-testid="burnout-question">
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <Badge variant="outline">Question {step} of 8</Badge>
            <Progress value={(step / 8) * 100} className="w-32" />
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle>{currentQuestion.label}</CardTitle>
              <CardDescription>{currentQuestion.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <Slider
              value={[answers[currentQuestion.key]]}
              onValueChange={(v) => handleAnswer(currentQuestion.key, v)}
              min={1}
              max={5}
              step={1}
              className="py-4"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{currentQuestion.lowLabel}</span>
              <span className="font-medium text-foreground">
                {answers[currentQuestion.key]} / 5
              </span>
              <span>{currentQuestion.highLabel}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={step === 1}>
              Back
            </Button>
            <Button className="flex-1" onClick={() => setStep(step + 1)}>
              {step === 8 ? "Continue" : "Next"}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Notes Screen (step 9)
  if (step === 9) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Additional Notes (Optional)</CardTitle>
          <CardDescription>
            Is there anything else you would like to share about how you are feeling?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Share any additional thoughts, concerns, or specific challenges you are facing..."
            rows={5}
          />

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(8)}>
              Back
            </Button>
            <Button className="flex-1" onClick={submitAssessment} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Submit Assessment
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Results Screen (step 10)
  if (step === 10 && result) {
    return (
      <Card className="max-w-2xl mx-auto" data-testid="burnout-results">
        <CardHeader className="text-center">
          <div className={`w-20 h-20 rounded-full ${RISK_COLORS[result.risk_level]} flex items-center justify-center mx-auto mb-4`}>
            {result.risk_level === "low" ? (
              <CheckCircle className="w-10 h-10 text-white" />
            ) : result.risk_level === "critical" ? (
              <AlertTriangle className="w-10 h-10 text-white" />
            ) : (
              <Activity className="w-10 h-10 text-white" />
            )}
          </div>
          <CardTitle className="text-2xl">
            Your Burnout Risk: <span className={RISK_TEXT_COLORS[result.risk_level]}>{result.risk_level.toUpperCase()}</span>
          </CardTitle>
          <CardDescription>
            Score: {result.total_score} out of 40
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Recommendations */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Recommendations
            </h3>
            <ul className="space-y-2">
              {result.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          {result.resources && result.resources.length > 0 && (
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Helpful Resources
              </h3>
              <div className="grid gap-2">
                {result.resources.map((resource, idx) => (
                  <div key={idx} className="p-3 border rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{resource.name}</p>
                      <p className="text-xs text-muted-foreground">{resource.description}</p>
                    </div>
                    {resource.type === "phone" && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={`tel:${resource.value}`}>
                          <Phone className="w-4 h-4 mr-1" />
                          Call
                        </a>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.follow_up_needed && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-orange-800">Follow-up Recommended</p>
                  <p className="text-sm text-orange-700">
                    Your care team has been notified and will reach out to provide additional support.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={resetAssessment}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Take Again
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => window.location.href = "/resources"}>
              <BookOpen className="w-4 h-4 mr-2" />
              View Resources
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}
